/**
 * Image Columns module
 *
 * Three columns image andn copy content
 */

// Deps
import { FelaComponent, createComponent } from "react-fela";
import { combineRules } from "fela";
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { textStyles } from "~/styleguide/typography";

// Components
import Visual from "~/components/shared/visual";
import { ContentMaxWidth } from "~/components/layout/max-width";

export default function ImageColumns({ block }) {
    const { heading, heading_copy, columns, anchor_id, preload } = block;

    return (
        <Container id={anchor_id} className="module-image-columns">
            <ContentMaxWidth>
                {/* Heading */}
                { (heading || heading_copy) && (
                    <FelaComponent
                        as="div"
                        style={componentHeadingContainer}
                    >
                        {heading && (
                            <FelaComponent
                                as="h3"
                                style={combineRules(
                                    textStyles.heading3,
                                    componentHeading
                                )}
                            >
                                <span dangerouslySetInnerHTML={{ __html: heading }} />
                            </FelaComponent>
                        )}

                        {heading_copy && (
                            <FelaComponent
                                as="p"
                                style={combineRules(
                                    textStyles.paragraphLarge,
                                    componentHeadingCopy
                                )}
                            >
                                <span dangerouslySetInnerHTML={{ __html: heading_copy }} />
                            </FelaComponent>
                        )}
                    </FelaComponent>
                )}


                <Columns
                    className="image-column"
                >
                    {columns &&
                        columns.length &&
                        columns.map((column, index) => {
                            return (
                                <Column count={columns.length} key={index}>
                                    {/* Image */}
                                    {column.image && (
                                        <ImageContainer>
                                            <Visual
                                              image={column.image}
                                              load={preload}
                                            />
                                        </ImageContainer>
                                    )}

                                    {/* Heading */}
                                    {column.heading && (
                                        <FelaComponent
                                            as="h4"
                                            style={combineRules(
                                                textStyles.resourceHeading,
                                                imageColHeading
                                            )}
                                        >
                                            {column.heading}
                                        </FelaComponent>
                                    )}

                                    {/* Copy */}
                                    {column.copy && (
                                        <Copy
                                            extend={copyStyles}
                                            dangerouslySetInnerHTML={{
                                                __html: column.copy
                                            }}
                                        ></Copy>
                                    )}
                                </Column>
                            );
                        })}
                </Columns>
            </ContentMaxWidth>
        </Container>
    );
}

const Container = createComponent(
    () => ({
        textAlign: "center",

        extend: [
            fluid("paddingV", gutters.vertical),
            fluid("paddingH", gutters.default)
        ]
    }),
    "div",
    ["id"]
);

const Columns = createComponent(() => ({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center"
}));

const Column = createComponent(({ count }) => ({
    minWidthMobileLandscape: {
        grid: {
            cols: "3",
            margins: "30",
            length: count,
            bottomMargin: 75
        }
    },
    maxWidthMobileLandscape: {
        width: "100%",
        maxWidth: 300,
        marginH: "auto",
        marginBottom: 50,
        grid: {
            cols: "3",
            margins: "30",
            length: count,
            bottomMargin: 30,
            lastElem: "auto"
        }
    }
}));

const ImageContainer = createComponent(() => ({
    position: "relative",
    width: "100%",
    maxWidth: 180,
    margin: "auto",
    "& .vv-visual": { height: "100%", width: "100%" },

    "& img": {
        width: "auto",
        margin: "auto",
        display: "block",
        overflow: "hidden"
    }
}));

const Copy = createComponent(() => textStyles.paragraphSmall, "p", [
    "dangerouslySetInnerHTML"
]);

const copyStyles = {
    lineHeight: "150%"
};

const componentHeadingContainer = {
    extend: [fluid("marginBottom", { min: 40, max: 80 })]
};

const componentHeading = {
    marginH: "auto"
};

const componentHeadingCopy = {
    marginH: "auto",
    extend: [fluid("marginTop", { min: 20, max: 40 })]
};

const imageColHeading = {
    marginTop: 20,
    marginBottom: 12
};
